import { Trans } from 'react-i18next';
import { get } from 'lodash';
import { GlobalToken } from 'antd';

import Super from '@/comp/Super';
import DissolvedOxygenIcon from '@/comp/common/icons/DissolvedOxygenIcon';
import PowerKpiIcon from '@/comp/common/icons/PowerKpiIcon';
import FlowKpiIcon from '@/comp/common/icons/FlowKpiIcon';
import PressureKpiIcon from '@/comp/common/icons/PressureKpiIcon';

// TO DO - Implement Dissolved Oxygen KPI
const getDissolvedOxygenKPI = (dissolvedOxygen, t, token) => ({
  key: 'dissolvedOxygen',
  title: t('dissolvedOxygen'),
  icon: <DissolvedOxygenIcon color={token.colorBorder} width={30} height={30} />,
  value: dissolvedOxygen?.currentValue,
  scale: {
    values: [0, dissolvedOxygen?.currentValue, dissolvedOxygen?.upperRange],
    colors: [token.colorError, token.colorSuccess, token.colorWarning],
    fadedColors: [token.colorErrorBorder, token.colorSuccessBorder, token.colorWarningBorder],
    shouldShowEdgeOnly: true,
  },
  hideTooltip: true,
});

const getPressureKPI = (pressure, t, tvalue, tunit, token) => ({
  key: 'pressure',
  title: t('PRESSURE'),
  icon: <PressureKpiIcon />,
  value: tvalue(pressure?.currentValue, 'bar'),
  unit: t(tunit('Bar')),
  scale: {
    values: [
      tvalue(pressure?.lowerRange, 'bar'),
      tvalue(pressure?.targetMinThreshold, 'bar'),
      tvalue(pressure?.targetMaxThreshold, 'bar'),
      tvalue(pressure?.upperRange, 'bar'),
    ],
    colors: [token.colorError, token.colorSuccess, token.colorWarning],
    fadedColors: [token.colorErrorBorder, token.colorSuccessBorder, token.colorWarningBorder],
  },
  targetValue: tvalue(pressure?.targetValue, 'bar'),
});

const getFlowKPI = (flow, t, tvalue, tunit, token) => ({
  key: 'flow',
  title: t('FLOW'),
  icon: <FlowKpiIcon />,
  value: tvalue(flow?.currentValue, 'm^3/min'),
  unit: (
    <Trans i18nKey={tunit('m3/min')}>
      <Super />
    </Trans>
  ),
  tooltip: t(
    flow?.estimatedFlow ? 'Compressor kW based calculation' : 'Flow based on Flow sensor avg at the end of the station',
  ),
  scale: {
    values: [
      tvalue(flow?.lowerRange, 'm^3/min'),
      tvalue(flow?.expectedRangeLow, 'm^3/min'),
      tvalue(flow?.expectedRangeHigh, 'm^3/min'),
      tvalue(flow?.upperRange, 'm^3/min'),
    ],
    colors: [token.colorWarning, token.colorSuccess, token.colorError],
    fadedColors: [token.colorWarningBorder, token.colorSuccessBorder, token.colorErrorBorder],
    shouldShowEdgeOnly: true,
  },
});

const getPowerKPI = (power, t, tvalue, token) => ({
  key: 'power',
  title: t('POWER'),
  icon: <PowerKpiIcon />,
  value: tvalue(power?.currentValue, 'kw'),
  unit: t('kW'),
  scale: {
    values: [
      tvalue(power?.lowerRange, 'kw'),
      tvalue(power?.flowHighEfficientRangeHigh(), 'kw'),
      tvalue(power?.flowMidEfficientRangeHigh(), 'kw'),
      tvalue(power?.upperRange, 'kw'),
    ],
    colors: [token.colorSuccess, token.colorWarning, token.colorError],
    fadedColors: [token.colorSuccessBorder, token.colorWarningBorder, token.colorErrorBorder],
    shouldShowEdgeOnly: true,
  },
});

const MapKPIConfig = (stationStore, t, tvalue, tunit, token: GlobalToken) => {
  const { pressure, flow, power, dissolvedOxygen } = get(stationStore, 'currentStation.kpis', {});

  const stationSpecificKPIs = stationStore.isWasteWaterStation
    ? [getDissolvedOxygenKPI(dissolvedOxygen, t, token)]
    : [getPressureKPI(pressure, t, tvalue, tunit, token)];

  return [...stationSpecificKPIs, getFlowKPI(flow, t, tvalue, tunit, token), getPowerKPI(power, t, tvalue, token)];
};

export default MapKPIConfig;
